import { useMutation, useQuery } from "@apollo/client/react";
import {
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";

import { DateRange } from "@mui/x-date-pickers-pro";
import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { format, subDays } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BACKFILLS_STATUS } from "../../constants/BackfillStatus";
import Permissions from "../../constants/permissions";
import { BACKFILLS_LIST_QUERY } from "../../graphql";
import type {
  BackfillsQuery,
  BackfillsQueryVariables,
  SaveBackfillMutation,
  SaveBackfillMutationVariables,
} from "../../graphql/types";
import { BackfillsDocument, SaveBackfillDocument } from "../../graphql/types";
import { getFormattedDate, getFormattedReportDate } from "../../lib/Util";
import BackfillStatus from "../BackfillDetails/BackfillStatus";
import { createDefaultBackfillFilters } from "../BackfillFilters";
import DateRangePicker from "../Date/DateRangePicker";
import { IfPermission } from "../Permissions";
import { UserAvatarWithTooltip } from "../ui/UserAvatar";
import { StatefulButton } from "../ui/muiButtons";
import { MenuItemStyled } from "../ui/rolesChip";
const customNameOptions = getGridStringOperators().filter(({ value }) =>
  ["equals", "contains", "startsWith"].includes(value)
);

const ViewDetailsIconStyled = styled(MagnifyingGlassIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));
const Container = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  width: "100%",
  height: "800px",
  backgroundColor: theme.palette.surfaceLight10.main,
  borderRadius: "8px",
}));

const CopyToClipboardItem = ({ backfillId }: { backfillId: string }) => {
  return (
    <CopyToClipboard text={`Backfill ID: ${backfillId}`} onCopy={() => toast.success("Copied to clipboard")}>
      <MenuItem>
        <ListItemIcon>
          <CopyIcon />
        </ListItemIcon>
        Copy backfill ID
      </MenuItem>
    </CopyToClipboard>
  );
};
const BackfillOptions = ({
  backfillId,
  backfillName,
  setSelectedBackfillToCancel,
  backfillStatus,
}: {
  backfillId: string;
  backfillName: string;
  setSelectedBackfillToCancel: (value: { backfillId: string; backfillName: string }) => void;
  backfillStatus: BACKFILLS_STATUS;
}) => {
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setShowOptionsMenu(false);
        }}
      >
        <IconButton
          ref={anchorEl}
          onClick={(e) => {
            e.stopPropagation();
            setShowOptionsMenu(true);
          }}
          aria-label="show backfill options"
        >
          <DotsThreeVerticalIcon />
        </IconButton>
      </ClickAwayListener>
      <Menu
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorEl={() => anchorEl.current}
        open={showOptionsMenu}
        onClose={() => setShowOptionsMenu(false)}
      >
        <CopyToClipboardItem backfillId={backfillId} />
        {backfillStatus === BACKFILLS_STATUS.Running || backfillStatus === BACKFILLS_STATUS.Preparing ? (
          <IfPermission permission={Permissions.Backfill}>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBackfillToCancel({
                  backfillId: backfillId,
                  backfillName: backfillName,
                });
              }}
            >
              <ListItemIcon aria-label="cancel backfill">
                <XIcon color="error" />
              </ListItemIcon>
              Cancel backfill
            </MenuItem>
          </IfPermission>
        ) : null}
        <MenuItem
          onClick={() => {
            navigate(`../details/${backfillId}?backfillName=${backfillName}&currentTab=Connections`);
          }}
        >
          <ListItemIcon aria-label={`view details of ${backfillName}`}>
            <ViewDetailsIconStyled />
          </ListItemIcon>
          View details
        </MenuItem>
      </Menu>
    </>
  );
};

export const BackfillTable = () => {
  const PAGE_SIZE = 20;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedBackfillToCancel, setSelectedBackfillToCancel] = useState<null | {
    backfillId: string;
    backfillName: string;
  }>(null);
  const [filter, setFilter] = useState(
    createDefaultBackfillFilters({
      status: searchParams.get("status"),
      createdRange: {
        start: searchParams.get("createdAfter"),
        end: searchParams.get("createdBefore"),
      },
      reportRange: {
        start: searchParams.get("reportStart"),
        end: searchParams.get("reportEnd"),
      },
    })
  );
  useEffect(() => {
    const { status, createdRange, reportRange } = filter;
    setSearchParams({
      ...(createdRange?.start && createdRange?.end
        ? {
            createdAfter: format(createdRange.start, "yyyy-MM-dd"),
            createdBefore: format(createdRange.end, "yyyy-MM-dd"),
          }
        : {}),
      ...(status ? { status } : {}),
      ...(reportRange?.start && reportRange?.end
        ? {
            reportStart: format(reportRange.start, "yyyy-MM-dd"),
            reportEnd: format(reportRange.end, "yyyy-MM-dd"),
          }
        : {}),
    });
  }, [filter, setSearchParams]);
  const formatCreatedRange = filter.createdRange.end
    ? `${format(filter.createdRange.end, "yyyy-MM-dd")}T23:59:59.000000Z`
    : undefined;
  const formatReportRange = filter.reportRange.end
    ? `${format(filter.reportRange.end, "yyyy-MM-dd")}T23:59:59.000000Z`
    : undefined;

  const variables = {
    status: filter.status,
    first: PAGE_SIZE,
    created_datetime_after: filter.createdRange.start?.toISOString(),
    created_datetime_before: formatCreatedRange,
    report_datetime_after: filter.reportRange.start?.toISOString(),
    report_datetime_before: formatReportRange,
  };
  const { data, fetchMore, loading } = useQuery<BackfillsQuery, BackfillsQueryVariables>(BackfillsDocument, {
    variables: variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const [cancelBackfill, cancelBackfillState] = useMutation<SaveBackfillMutation, SaveBackfillMutationVariables>(
    SaveBackfillDocument,
    {
      refetchQueries: () => [BACKFILLS_LIST_QUERY],
      onCompleted: () => {
        toast.success("Backfill cancelled");
      },
      onError: () => {
        toast.error("Error when cancelling backfill");
      },
    }
  );

  const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (data?.backfills?.pageInfo?.hasNextPage === false) return;
    fetchMore({
      variables: {
        after: data?.backfills?.pageInfo?.endCursor,
      },
    });
  };
  const rows = data?.backfills?.edges ? data.backfills.edges.map((entry) => entry.node) : [];

  const BackfillColumns: GridColDef[] = [
    {
      width: 200,
      field: "report_date",
      headerName: "Report Date Range",
      renderCell: (params) => {
        const startDateFormat = getFormattedReportDate(params.row.start_datetime);
        const endDateFormat = getFormattedReportDate(params.row.end_datetime);
        return `${startDateFormat} - ${endDateFormat}`;
      },
      valueGetter: (_, row) => {
        const startDateFormat = getFormattedDate(row.start_datetime);
        const endDateFormat = getFormattedDate(row.end_datetime);
        return `${startDateFormat} - ${endDateFormat}`;
      },
      filterOperators: [
        {
          label: "is between",
          value: "is between",
          getApplyFilterFn: () => null,
          InputComponent: ({ item, applyValue }) => {
            return (
              <DateRangePicker
                disableFuture
                calendars={1}
                start={filter.reportRange.start ? filter.reportRange.start : subDays(new Date(), 7)}
                end={filter.reportRange.end ? filter.reportRange.end : new Date()}
                onChange={(value: DateRange<Date>) => {
                  applyValue({
                    ...item,
                    value: { start: value[0], end: value[1] },
                  });
                  setFilter({
                    ...filter,
                    reportRange: {
                      start: value[0] ?? undefined,
                      end: value[1] ?? undefined,
                    },
                  });
                }}
              />
            );
          },
        },
      ],
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      filterOperators: customNameOptions,
    },
    {
      field: "created_on",
      width: 200,
      headerName: "Created",
      editable: true,
      type: "date",
      renderCell: (params) => {
        const createdDay = getFormattedDate(params.row.created_on);
        return createdDay;
      },
      valueFormatter: (_, row) => {
        const createdDay = getFormattedDate(row.created_on);
        return createdDay;
      },
      filterable: true,
      filterOperators: [
        {
          label: "is between",
          value: "is between",
          getApplyFilterFn: () => null,
          InputComponent: ({ item, applyValue }) => {
            return (
              <DateRangePicker
                disableFuture
                calendars={1}
                start={filter.createdRange.start ? filter.createdRange.start : subDays(new Date(), 7)}
                end={filter.createdRange.end ? filter.createdRange.end : new Date()}
                onChange={(value) => {
                  applyValue({
                    ...item,
                    value: { start: value[0], end: value[1] },
                  });
                  setFilter({
                    ...filter,
                    createdRange: { start: value[0], end: value[1] },
                  });
                }}
              />
            );
          },
        },
      ],
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      type: "string",
      filterable: false,
      valueFormatter(_, row) {
        return row.created_by.name;
      },
      renderCell: (params) => {
        return (
          <>
            <UserAvatarWithTooltip
              user={params.row.created_by}
              prefix="Created by "
              sxIconButton={{
                marginLeft: "0",
                marginRight: "0",
                padding: "0",
              }}
              size="small"
            />
            <Typography sx={{ fontSize: 14, color: "text50.main", marginLeft: "12px" }}>
              {params.row.created_by.name}
            </Typography>
          </>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      editable: true,
      renderCell: (params) => {
        return (
          <Stack direction="row" sx={{ height: "100%", alignItems: "center" }}>
            <BackfillStatus
              status={params.row.status}
              totalPipelines={params.row.total_pipelines}
              succeededPipelines={params.row.succeeded_pipelines}
              failedPipelines={params.row.failed_pipelines}
              cancelledPipelines={params.row.cancelled_pipelines}
              hasDirtyRows={params.row.hasDirtyRows}
            />
          </Stack>
        );
      },
      filterOperators: [
        {
          label: "contains",
          value: "contains",

          getApplyFilterFn: () => null,
          InputComponent: ({ item, applyValue }) => (
            <>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                data-testid="status-select"
                variant="standard"
                value={item?.value || ""}
                onChange={(event) => {
                  applyValue({
                    ...item,
                    value: event.target.value as string,
                  });
                  setFilter({
                    ...filter,
                    status: event.target.value as string,
                  });
                }}
              >
                <MenuItemStyled key={BACKFILLS_STATUS.Completed} value={BACKFILLS_STATUS.Completed}>
                  Completed
                </MenuItemStyled>
                <MenuItemStyled key={BACKFILLS_STATUS.Cancelled} value={BACKFILLS_STATUS.Cancelled}>
                  Cancelled
                </MenuItemStyled>
                <MenuItemStyled key={BACKFILLS_STATUS.Preparing} value={BACKFILLS_STATUS.Preparing}>
                  Preparing
                </MenuItemStyled>
                <MenuItemStyled key={BACKFILLS_STATUS.Running} value={BACKFILLS_STATUS.Running}>
                  Running
                </MenuItemStyled>
                <MenuItemStyled key={BACKFILLS_STATUS.CompletedWithErrors} value={BACKFILLS_STATUS.CompletedWithErrors}>
                  Completed with errors
                </MenuItemStyled>
              </Select>
            </>
          ),
        },
      ],
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      disableExport: true,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <BackfillOptions
            backfillId={params.row.id}
            backfillName={params.row.title}
            setSelectedBackfillToCancel={setSelectedBackfillToCancel}
            backfillStatus={params.row.status}
          />
        );
      },
      filterable: false,
      hideSortIcons: true,
    },
  ];
  const updateFilter = (model: GridFilterModel) => {
    const filterByStatus = model.items.find((item) => item.field === "status");
    const filterByCreatedOn = model.items.find((item) => item.field === "created_on");
    const filterByReportDate = model.items.find((item) => item.field === "report_date");
    const newCreatedRange = filterByCreatedOn?.value
      ? {
          start: filterByCreatedOn?.value?.start,
          end: filterByCreatedOn?.value?.end,
        }
      : { start: undefined, end: undefined };
    const newReportRange = filterByReportDate?.value
      ? {
          start: filterByReportDate?.value?.start,
          end: filterByReportDate?.value?.end,
        }
      : { start: undefined, end: undefined };
    setFilter({
      createdRange: newCreatedRange,
      reportRange: newReportRange,
      status: filterByStatus?.value || "",
    });
  };

  return (
    <>
      <Container data-testid="backfills-table">
        <DataGridPro
          loading={loading}
          rows={rows}
          columns={BackfillColumns}
          disableColumnPinning
          disableDensitySelector
          onRowsScrollEnd={handleOnRowsScrollEnd}
          onFilterModelChange={updateFilter}
          sx={{
            minHeight: "100%",
          }}
          showToolbar
          slotProps={{
            cell: {
              "data-testid": "gridcell",
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
            row: {
              "data-testid": "backfill-row",
            },
            filterPanel: {
              logicOperators: [GridLogicOperator.And],
            },
            panel: {
              //@ts-ignore
              sx: {
                "& .MuiDataGrid-filterFormValueInput": {
                  width: "300px",
                  marginLeft: "8px",
                  justifyContent: "flex-end",
                },
              },
            },
          }}
          onRowClick={(params) => {
            navigate(`../details/${params.row.id}?backfillName=${params.row.title}&currentTab=Connections`);
          }}
        />
      </Container>
      <Dialog
        open={Boolean(selectedBackfillToCancel)}
        data-testid="confirm-cancel-dialog"
        onClose={() => {
          setSelectedBackfillToCancel(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="heading.four">
          Do you want to cancel backfill <b>{selectedBackfillToCancel?.backfillName}</b>?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setSelectedBackfillToCancel(null)} data-testid="cancel-button" variant="outlined">
            Cancel
          </Button>
          <StatefulButton
            onClick={() => {
              if (!selectedBackfillToCancel) {
                return;
              }
              cancelBackfill({
                variables: {
                  backfill_id: selectedBackfillToCancel?.backfillId,
                  status: BACKFILLS_STATUS.Cancelled,
                },
              });
              setSelectedBackfillToCancel(null);
            }}
            data-testid="confirm-button"
            loading={cancelBackfillState.loading}
            color="error"
          >
            Confirm
          </StatefulButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
